"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Check } from "lucide-react";
import Link from "next/link";
import { LogoMark } from "@/components/logo";
import { Button } from "@/components/ui/button";

const EASE = [0.16, 1, 0.3, 1] as const;

// The hero leads with the multi-location wedge: one shared playbook running at
// every location. We show only the positive Helios artifact here (sample data),
// a compact "every location in sync" rollup. Sample names are illustrative.
const LOCATIONS = [
	{ name: "River North", note: "Intro-offer follow-up" },
	{ name: "West Loop", note: "Intro-offer follow-up" },
	{ name: "Lincoln Park", note: "Intro-offer follow-up" },
	{ name: "Wicker Park", note: "Newest, live day one" },
] as const;

function HeroCopy() {
	const reduceMotion = useReducedMotion();
	const item = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
	};
	const container = {
		hidden: {},
		visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
	};
	const containerProps = reduceMotion
		? {}
		: {
				initial: "hidden" as const,
				animate: "visible" as const,
				variants: container,
			};
	const iv = reduceMotion ? undefined : item;

	return (
		<motion.div
			className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
			{...containerProps}
		>
			<motion.span
				variants={iv}
				className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
			>
				<Building2 className="size-4" />
				Built for multi-location brands and franchises
			</motion.span>

			<motion.h1
				variants={iv}
				className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
			>
				Run every location like your best one
			</motion.h1>

			<motion.p
				variants={iv}
				className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
			>
				As you add locations and franchisees, follow-up and retention start to
				drift. Helios rolls out your playbook to every location with visibility into what's working.
			</motion.p>

			<motion.div
				variants={iv}
				className="mt-8 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row xl:items-start"
			>
				<Button asChild size="xl" className="group w-full sm:w-auto">
					<Link href="/book-a-demo">
						Book a demo
						<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
					</Link>
				</Button>
				<Button
					asChild
					size="xl"
					variant="outline"
					className="w-full sm:w-auto"
				>
					<Link href="#visibility">See it in action</Link>
				</Button>
			</motion.div>

		</motion.div>
	);
}

function NetworkCard() {
	const reduceMotion = useReducedMotion();
	const rowAnim = (i: number) =>
		reduceMotion
			? {}
			: {
					initial: { opacity: 0, y: 10 },
					animate: { opacity: 1, y: 0 },
					transition: { duration: 0.6, ease: EASE, delay: 0.5 + i * 0.12 },
				};

	return (
		<div className="w-[300px] rounded-2xl border border-border bg-card p-5 shadow-xl sm:w-[332px]">
			<div className="flex items-center gap-2 border-b border-border pb-4">
				<span className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-primary dark:text-brand-accent">
					<LogoMark className="size-4" />
				</span>
				<div className="min-w-0 flex-1">
					<div className="text-sm font-bold leading-tight text-foreground">
						Helios
					</div>
					<div className="text-xs leading-tight text-muted-foreground">
						Every location in sync
					</div>
				</div>
				<span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
					4 locations
				</span>
			</div>

			<ul className="mt-4 space-y-2.5">
				{LOCATIONS.map(({ name, note }, i) => (
					<motion.li
						key={name}
						className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
						{...rowAnim(i)}
					>
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-semibold text-foreground">
								{name}
							</div>
							<div className="truncate text-xs text-muted-foreground">
								{note}
							</div>
						</div>
						<span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-green-600 dark:text-green-500">
							<Check strokeWidth={3} className="size-3.5" />
							In sync
						</span>
					</motion.li>
				))}
			</ul>

			<div className="mt-4 rounded-lg border border-border bg-background px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
				One standard, live at every location
			</div>
		</div>
	);
}

function Stage() {
	const reduceMotion = useReducedMotion();
	const cardAnim = reduceMotion
		? {}
		: {
				initial: { opacity: 0, x: 24, scale: 0.98 },
				animate: { opacity: 1, x: 0, scale: 1 },
				transition: { duration: 0.8, ease: EASE, delay: 0.3 },
			};

	return (
		<div className="relative flex w-full justify-center xl:justify-end">
			{/* Faded duplicate behind for depth — purely decorative. */}
			<div
				aria-hidden
				className="absolute left-1/2 top-6 hidden h-full w-[300px] -translate-x-1/2 rounded-2xl border border-border bg-muted/30 sm:w-[332px] xl:left-auto xl:right-6 xl:block xl:translate-x-0"
			/>
			<motion.div {...cardAnim} className="relative z-10">
				<NetworkCard />
			</motion.div>
		</div>
	);
}

export function MultiLocationHero() {
	return (
		<section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
			<div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)] xl:gap-10">
				<HeroCopy />
				<Stage />
			</div>
		</section>
	);
}
